import React from "react";
import {useQuery} from "react-query";
import {useTranslation} from "react-i18next";
import {AppService} from "crqlar-react-common/dist/services/app/app.service";
import {MyAccountService} from "crqlar-react-common/dist/services/identity/myAccount.service";
import {useOrganizationAppsQuery} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";
import {useHasGuestAppModule} from "crqlar-react-common/dist/utils/hooks/guestApp/useGuestAppModule";
import {useCanViewLogs} from "crqlar-react-common/dist/utils/hooks/logs/useCanViewLogs";
import {useAuthorizedViaAdmin} from "crqlar-react-common/dist/utils/hooks/admin/useAuthorizedViaAdmin";
import {AppCategory} from "crqlar-react-common/dist/models/app/app.model";
import {UserType, FULL_SETTINGS_ACCESS_USER_TYPES} from "crqlar-react-common/dist/models/identity/user.model";
import {getLocalizedValue} from "crqlar-react-common/dist/utils/translations.util";
import type {OrganizationApp} from "crqlar-react-common/dist/models/app/organizationApp.model";
import {CONFIGURABLE_INTEGRATION_SKUS} from "./integrations/integrationSkus";
import {
    SETTINGS_NAVIGATION_DEFINITIONS,
    type SettingsItem,
    type SettingsNavigationDefinition,
    type SettingsNavigationId,
    type SettingsPermission,
    type SettingsSection,
} from "./settings/settingsNavigation";

const FULL_ACCESS_USER_TYPES = FULL_SETTINGS_ACCESS_USER_TYPES

const SETTINGS_SECTION_ORDER = Array.from(new Set(
    SETTINGS_NAVIGATION_DEFINITIONS.map(route => route.sectionId)
))

const toItem = (route: SettingsNavigationDefinition): SettingsItem => ({
    id: route.id,
    label: route.label,
    path: route.path,
    description: route.description,
    createLabel: route.createLabel,
    createPath: route.createPath,
    keywords: route.keywords,
})

export const useSettingsAccess = () => {
    const {i18n} = useTranslation()

    const myAccountQ = useQuery(['myAccount'], () => MyAccountService.getMyAccount())
    const organizationAppsQ = useOrganizationAppsQuery()
    const marketplaceAppsQ = useQuery('allMarketplaceApps', () => AppService.getAll({
        limit: {offset: 0, limit: 1000, sort: [{field: "order", direction: "DESC"}]},
    }))
    const {hasGuestAppModule, isLoading: guestAppModuleLoading} = useHasGuestAppModule()
    const {canViewLogs, isLoading: logsLoading} = useCanViewLogs()
    const isAuthorizedViaAdmin = useAuthorizedViaAdmin()

    const organizationApps = organizationAppsQ.data?.content
    const marketplaceApps = marketplaceAppsQ.data?.content
    const permittedAppIds = myAccountQ.data?.permittedAppIds
    const userType = myAccountQ.data?.userType
    const isLoading =
        myAccountQ.isLoading ||
        organizationAppsQ.isLoading ||
        marketplaceAppsQ.isLoading ||
        guestAppModuleLoading ||
        logsLoading

    const access = React.useMemo(() => {
        const orgApps = organizationApps || []
        const apps = marketplaceApps || []

        const hasFullAccess =
            permittedAppIds == null ||
            FULL_ACCESS_USER_TYPES.includes(userType as UserType)

        const canAccessOrgApp = (orgApp: OrganizationApp) =>
            hasFullAccess || Boolean(permittedAppIds?.includes(orgApp.id as string))

        const accessibleApps = orgApps
            .filter(canAccessOrgApp)
            .map(orgApp => apps.find(app => app.id === orgApp.appId))
            .filter((app): app is NonNullable<typeof app> => Boolean(app))
        const canAccessPermissions = (permissions: readonly SettingsPermission[]) =>
            !permissions.length ||
            hasFullAccess ||
            accessibleApps.some(app => permissions.some(permission => app.capabilities?.includes(permission)))

        const canAccessAppSku = (sku: string) => orgApps
            .filter(canAccessOrgApp)
            .some(orgApp => apps.some(app => app.id === orgApp.appId && app.sku === sku))

        const navigationItemVisible = (route: SettingsNavigationDefinition) => {
            if (route.requiresGuestAppModule && !hasGuestAppModule) return false
            if (route.requiresCanViewLogs && !canViewLogs) return false
            if (route.requiresAdmin && !isAuthorizedViaAdmin) return false
            if (route.requiredAppSku && !canAccessAppSku(route.requiredAppSku)) return false
            return canAccessPermissions(route.requiredPermissions)
        }

        const canAccessSettingsItem = (settingsId: SettingsNavigationId) => {
            const route = SETTINGS_NAVIGATION_DEFINITIONS.find(candidate => candidate.id === settingsId)
            return !route || navigationItemVisible(route)
        }

        const integrationItems: SettingsItem[] = orgApps
            .map((orgApp): SettingsItem | null => {
                const appInfo = apps.find(app => app.id === orgApp.appId)
                if (appInfo?.category !== AppCategory.INTEGRATION) return null
                if (!CONFIGURABLE_INTEGRATION_SKUS.has(appInfo.sku as string)) return null
                if (!canAccessOrgApp(orgApp)) return null
                return {
                    id: `integration-${orgApp.id}`,
                    label: orgApp.name || appInfo.name,
                    path: `/system/integrations/${appInfo.sku}`,
                    description: getLocalizedValue(i18n.language, appInfo.descriptionLocalized),
                }
            })
            .filter((item): item is SettingsItem => item !== null)

        const sections: SettingsSection[] = SETTINGS_SECTION_ORDER
            .map(sectionId => {
                const routes = SETTINGS_NAVIGATION_DEFINITIONS.filter(route => route.sectionId === sectionId && navigationItemVisible(route))
                return {
                    id: sectionId,
                    label: routes[0]?.sectionLabel || sectionId,
                    items: routes.map(toItem),
                }
            })
            .filter(section => section.items.length > 0)

        if (integrationItems.length) {
            sections.push({id: 'integrations', label: 'Integrations', items: integrationItems})
        }

        return {sections, canAccessSettingsItem, canAccessAppSku}
    }, [organizationApps, marketplaceApps, permittedAppIds, userType, hasGuestAppModule, canViewLogs, isAuthorizedViaAdmin, i18n.language])

    return {isLoading, hasGuestAppModule, ...access}
}
